import { useMemo } from "react";
import { AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

/**
 * Banner globale mostrato quando l'abbonamento è in stato "past_due" ma ancora
 * dentro il grace period (3 giorni). Se il grace è scaduto, l'API restituisce
 * 402 e l'app viene già ridirezionata dalla pagina di abbonamento (interceptor).
 */
export default function BillingBanner() {
  const { branding, user } = useAuth();
  const nav = useNavigate();
  const bs = branding?.billing_status;

  const info = useMemo(() => {
    if (!bs || !bs.in_grace_period) return null;
    const endsAt = bs.grace_period_ends_at ? new Date(bs.grace_period_ends_at) : null;
    let deadline = "";
    let hoursLeft = null;
    if (endsAt) {
      deadline = endsAt.toLocaleString("it-IT", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
      hoursLeft = Math.max(0, Math.ceil((endsAt.getTime() - Date.now()) / 3_600_000));
    }
    return { deadline, hoursLeft };
  }, [bs]);

  if (!info) return null;
  const isAgente = user?.role === "agente";

  return (
    <div
      data-testid="billing-grace-banner"
      className="w-full bg-amber-50 border-b border-amber-200"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-2.5 flex items-center gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
        <div className="flex-1 text-sm text-amber-900 leading-snug">
          <span className="font-semibold">{bs.bonifico_overdue ? "Canone scaduto." : "Pagamento non riuscito."}</span>{" "}
          {bs.bonifico_overdue ? (
            <>
              Effettua il bonifico del canone entro il{" "}
              <span className="font-semibold">{info.deadline}</span>, altrimenti l&apos;accesso alla piattaforma verrà sospeso.
            </>
          ) : isAgente ? (
            <>
              Aggiorna il metodo di pagamento entro il{" "}
              <span className="font-semibold">{info.deadline}</span>
              {info.hoursLeft != null && info.hoursLeft <= 72 ? (
                <> (~{info.hoursLeft}h residue)</>
              ) : null}
              , altrimenti l&apos;accesso alla piattaforma verrà sospeso.
            </>
          ) : (
            <>
              L&apos;abbonamento della tua agenzia è in ritardo di pagamento. Chiedi
              all&apos;agente di regolarizzare entro il{" "}
              <span className="font-semibold">{info.deadline}</span> per evitare
              la sospensione dell&apos;accesso.
            </>
          )}
        </div>
        {isAgente ? (
          <Button
            size="sm"
            data-testid="billing-banner-cta"
            onClick={() => nav("/admin/abbonamento")}
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            Vai a Abbonamento
          </Button>
        ) : null}
      </div>
    </div>
  );
}
